import * as amplitude from "@amplitude/analytics-browser";
import {
  Center,
  Divider,
  FlexProps,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import _ from "lodash";
import { useCallback, useMemo, useState } from "react";
import { RiFilePaper2Line, RiMore2Line } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { FormTabsTypes, SearchAndFilterTypes } from "src/constants";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useDefaultColumnsProps } from "src/hooks/useDefaultColumnsProps";
import { useNavLinkState } from "src/hooks/useNavLinkState";
import { useOrganization } from "src/hooks/useOrganization";
import {
  extractSavedViewFilters,
  formatSavedViewParams,
  SavedView,
  SavedViewFormType,
  SharePermission,
  useSavedViews,
} from "src/hooks/useSavedViews";
import { verifyIfFiltersAreApplied } from "src/scenes/orgAdmin/forms/formFilters/utils";
import { getDefaultColumnIdsForTab } from "src/scenes/orgAdmin/forms/formTables/columns";
import { deprecatedColumnKeyToCurrentColumnKey } from "src/scenes/orgAdmin/forms/formTables/constants";
import { maybePluralize } from "src/services/format";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { SaveViewButton } from "../Buttons/SaveViewButton";
import { ShareSavedView } from "../Buttons/ShareSavedView";
import { ManageSavedViewDialog } from "../Dialogs/ManageSavedViewDialog";
import { useFlags } from "../Providers/FeatureFlagProvider";
import { NavItemElement, NavItemIconType } from "./NavItem";
import { NavLinkItem } from "./NavLinkItem";

export type NavGroupElement = React.ReactElement<Props>;
type NavItemNode = NavItemElement | boolean | undefined | null;
type Props = {
  kind?: "NavGroupElement";
  label?: string;
  trailIcon?: NavItemIconType;
  children: NavItemNode | NavItemNode[];
  level?: number;
} & FlexProps;

export function SavedViews({
  formTemplate,
}: {
  formTemplate: GQL.GetFormTemplatesByForLeftNavigation_form_template;
}) {
  const [editingSavedView, setEditingSavedView] = useState<SavedView>();
  const [searchParams] = useSearchParams();
  const flags = useFlags(["saved-views-sharing", "saved-view-custom-columns"]);
  const sharedSavedViewsEnabled = flags["saved-views-sharing"].enabled;
  const savedViewCustomColumnsEnabled =
    flags["saved-view-custom-columns"].enabled;

  const formTemplateId = formTemplate.id;
  const formTemplateName = formTemplate.name;

  const defaultColumnsProps = useDefaultColumnsProps({ formTemplate });

  const {
    savedViews,
    handleAddSavedView,
    handleUpdateSavedView,
    handleDeleteSavedView,
  } = useSavedViews({
    formTemplateId,
    defaultColumnsProps,
  });

  const organization = useOrganization();
  const formatUrl = (savedView?: SavedView) =>
    organization
      .map((org) =>
        Url.OrgAdmin.Forms.index({
          organization: org,
          formTemplateId,
          params:
            savedView && formatSavedViewParams(savedView, defaultColumnsProps),
        })
      )
      .withDefault("#");

  const {
    confirm: confirmSavedViewDeletion,
    confirmationDialog: deleteConfirmationDialog,
  } = useConfirmationDialog({
    header: "Delete view?",
    body: (
      <Text>
        Deleting this view will permanently remove it from the main navigation.
        This action cannot be undone or recovered.
      </Text>
    ),
    cancelButton: { label: "Cancel" },
    confirmButton: { label: "Yes, delete" },
  });

  const handleDelete = useCallback(
    async (savedView: SavedView) => {
      const confirmed = await confirmSavedViewDeletion({
        body: (
          <Text>
            Deleting this view will permanently remove it from the main
            navigation
            {sharedSavedViewsEnabled &&
              savedView.sharedCount > 0 &&
              ` for all ${savedView.sharedCount} ${maybePluralize(
                savedView.sharedCount,
                "member"
              )} and/or ${maybePluralize(
                savedView.sharedCount,
                "team"
              )} with current access`}
            . This action cannot be undone or recovered.
          </Text>
        ),
      });

      if (confirmed) {
        amplitude.track("Confirmed deleting view");
        handleDeleteSavedView(savedView.id);
      } else {
        amplitude.track("Clicked cancel");
      }
    },
    [confirmSavedViewDeletion, handleDeleteSavedView, sharedSavedViewsEnabled]
  );

  const handleSave = useCallback(
    (savedView: SavedViewFormType) => {
      if (editingSavedView?.id) {
        handleUpdateSavedView(savedView, editingSavedView.id);
      } else {
        handleAddSavedView(savedView);
      }
      setEditingSavedView(undefined);
    },
    [handleAddSavedView, handleUpdateSavedView, editingSavedView]
  );

  const isFormListActive = useNavLinkState(formatUrl(), []) === "active";

  const shouldShowSaveButton = useMemo(() => {
    if (!isFormListActive) return false;

    const defaultColumns = getDefaultColumnIdsForTab({
      tabType:
        (searchParams.get(SearchAndFilterTypes.Tab) as FormTabsTypes) ||
        FormTabsTypes.All,
      ...defaultColumnsProps,
    });

    const currentFilters = extractSavedViewFilters(
      searchParams,
      savedViewCustomColumnsEnabled,
      defaultColumnsProps
    );
    const savedViewId = searchParams.get(SearchAndFilterTypes.SavedView);
    const savedViewFilters = savedViews.find(
      (view) => view.id === savedViewId
    )?.filters;
    if (savedViewFilters) {
      const savedViewParams = savedViewFilters.params;
      // Handle backward compatibility for deprecated column keys in saved view sort key param
      const savedViewSortKey = savedViewParams[SearchAndFilterTypes.SortKey];
      if (savedViewSortKey) {
        const updatedSortKey =
          deprecatedColumnKeyToCurrentColumnKey[savedViewSortKey];
        if (updatedSortKey) {
          savedViewParams[SearchAndFilterTypes.SortKey] = updatedSortKey;
        }
      }
      const paramsMatch = _.isEqual(
        currentFilters.params,
        savedViewFilters.params
      );
      let columnsMatch = true;
      if (savedViewCustomColumnsEnabled) {
        if (savedViewFilters.columns) {
          columnsMatch = _.isEqual(
            currentFilters.columns,
            savedViewFilters.columns
          );
        } else {
          // If saved view custom column FF is enabled and savedViewFilters.columns doesn't exist,
          // assume the saved view includes all default columns.
          columnsMatch = _.isEqual(currentFilters.columns, defaultColumns);
        }
      }
      return !paramsMatch || !columnsMatch;
    }
    // Hack to prevent churn when the page first loads.
    if (!searchParams.has(SearchAndFilterTypes.Tab)) return false;

    return (
      searchParams.get(SearchAndFilterTypes.Tab) !== FormTabsTypes.All ||
      searchParams.get(SearchAndFilterTypes.Search) ||
      verifyIfFiltersAreApplied(searchParams) ||
      (savedViewCustomColumnsEnabled &&
        !_.isEqual(currentFilters.columns, defaultColumns))
    );
  }, [
    defaultColumnsProps,
    isFormListActive,
    savedViewCustomColumnsEnabled,
    savedViews,
    searchParams,
  ]);

  const sortedSavedViews = useMemo(() => {
    return _.sortBy(savedViews, (savedView) => savedView.name.toLowerCase());
  }, [savedViews]);

  return (
    <>
      {sortedSavedViews.map((savedView) => (
        <SavedViewItem
          key={savedView.id}
          savedView={savedView}
          onEdit={setEditingSavedView}
          onDelete={handleDelete}
          formTemplateName={formTemplateName}
          url={formatUrl(savedView)}
        />
      ))}
      {shouldShowSaveButton && (
        <SaveViewButton
          onSave={handleSave}
          formTemplateName={formTemplateName}
        />
      )}
      {deleteConfirmationDialog}
      {editingSavedView && (
        <ManageSavedViewDialog
          savedView={editingSavedView}
          isOpen={!!editingSavedView}
          onCancel={() => setEditingSavedView(undefined)}
          onSave={handleSave}
        />
      )}
    </>
  );
}

type SavedViewItemProps = {
  savedView: SavedView;
  onEdit: (savedView: SavedView) => void;
  onDelete: (savedView: SavedView) => void;
  formTemplateName: string;
  url: string;
};

function SavedViewItem({
  savedView,
  onEdit,
  onDelete,
  formTemplateName,
  url,
}: SavedViewItemProps) {
  return (
    <NavLinkItem
      label={savedView.name}
      applyGlossary={false}
      leadIcon={
        savedView.icon
          ? // This is a hack to use a text element as an icon.
            (props: any) => (
              <Center fontSize="lg" {...props}>
                {savedView.icon}
              </Center>
            )
          : RiFilePaper2Line
      }
      url={url}
      matchUrlParams={[SearchAndFilterTypes.SavedView]}
      menu={() => (
        <SavedViewMenu
          savedView={savedView}
          onEdit={onEdit}
          onDelete={onDelete}
          formTemplateName={formTemplateName}
        />
      )}
    />
  );
}

type SavedViewMenuProps = {
  savedView: SavedView;
  onEdit: (savedView: SavedView) => void;
  onDelete: (savedView: SavedView) => void;
  formTemplateName: string;
};

function SavedViewMenu({
  savedView,
  onEdit,
  onDelete,
  formTemplateName,
}: SavedViewMenuProps) {
  const flags = useFlags(["saved-views-sharing"]);
  const sharedSavedViewsEnabled = flags["saved-views-sharing"].enabled;
  const permission = savedView.permission;
  return permission === SharePermission.Viewer ? null : (
    <Menu placement="bottom-end">
      <MenuButton
        as={IconButton}
        variant="banner"
        size="xs"
        icon={<RiMore2Line />}
      />

      <MenuList color="gray.700" fontSize="sm">
        <MenuItem
          key={`edit-${savedView.id}`}
          onClick={() => {
            amplitude.track("Clicked edit view", {
              form_name: formTemplateName,
            });
            onEdit(savedView);
          }}
        >
          Edit
        </MenuItem>

        {sharedSavedViewsEnabled && (
          <ShareSavedView
            savedView={savedView}
            formTemplateName={formTemplateName}
          />
        )}

        {savedView.permission === "Owner" && (
          <>
            <Divider />
            <MenuItem
              key={`delete-${savedView.id}`}
              onClick={() => {
                amplitude.track("Clicked delete view", {
                  form_name: formTemplateName,
                });
                onDelete(savedView);
              }}
            >
              Delete
            </MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  );
}
